import type { SystemMessageType } from 'src/service/messaging'

export const SIGNAL_R_MESSAGES = {
  updatedFamily: 'updatefamily',
  updatedStaff: 'updatestaff',
  updatedRegistration: 'updateregistration',
  updatedProcessing: 'updateprocessing',
  portalRegistrationCreated: 'portalRegistrationCreated',
  getAttendance: 'getAttendance',
  getMedicationList: 'getMedicationList',
  newNotification: 'newNotification',
  clearedNotification: 'clearedNotification',
  newMessage: 'newMessage',
  urgentMessage: 'urgentMessage',
  messageRead: 'messageRead',
  messageUnread: 'messageUnread',
  messageEdited: 'messageEdited',
  groupChatSystemMessage: 'groupChatSystemMessage',
  newAnnouncement: 'newAnnouncement',
  broadcastSendingComplete: 'broadcastSendingComplete',
} as const

export type SignalRMessages =
  (typeof SIGNAL_R_MESSAGES)[keyof typeof SIGNAL_R_MESSAGES]

export const SIGNAL_R_SCOPES = {
  system: 'system',
  notification: 'notification',
  message: 'message',
} as const

export type SignalRScopes =
  (typeof SIGNAL_R_SCOPES)[keyof typeof SIGNAL_R_SCOPES]

export type SystemResponse = {
  Event: SignalRMessages
  Sender: string
  Target: string
  Text: string
  TimeStamp: string
  OrgId?: string
  CenterId?: string
}

export type SignalREvents = {
  [K in keyof typeof SIGNAL_R_SCOPES]: SystemResponse | null
}

// ========================
// Messaging SignalR Events (user-level connection)
// ========================

/**
 * SignalR method names sent by the messaging service on the user-level connection.
 * These are registered directly as connection.on('methodName', handler).
 */
export const MESSAGING_SIGNALR_METHODS = {
  newMessage: 'newMessage',
  urgentMessage: 'urgentMessage',
  messageRead: 'messageRead',
  messageUnread: 'messageUnread',
  messageEdited: 'messageEdited',
  groupChatSystemMessage: 'groupChatSystemMessage',
  newAnnouncement: 'newAnnouncement',
  broadcastSendingComplete: 'broadcastSendingComplete',
} as const

/** Payload for newMessage / urgentMessage events */
export type NewMessageSignalRPayload = {
  conversationId: string
  messageId: string
  senderDisplayName: string
  senderUserId?: string
  senderActingAsGroupName: string | null
  content: string
  timestamp: string
  scope: string
  isUrgent: boolean
  // Present on broadcast messages instead of conversationId/messageId
  broadcastMessageId?: string
  // When true, this event is only for refreshing an open conversation view —
  // no inbox, unread, or toast updates should occur.
  viewerOnly?: boolean
  // For INTERNAL scope: the formatted partner name for inbox display (e.g. "Toddlers (via Admin Team)")
  partnerName?: string
}

export type GroupChatSystemMessageSignalRPayload = {
  id: string
  conversationId: string
  senderUserId: string
  senderActingAsGroupId: string | null
  senderActingAsGroupName: string | null
  content: string
  timestamp: string
  kind: 'system'
  systemMessageType: SystemMessageType
  actorUserId: string | null
  actorDisplayName: string | null
  targetDisplayNames: string[] | null
  systemMetadata: Record<string, string> | null
  isRead: boolean
  readAt: string | null
  editedAt: string | null
  broadcastMessageId: string | null
  isOwnMessage: boolean
  isUrgent: boolean
  isLegacy: boolean
  displayName: string
}

/** Payload for messageRead events */
export type MessageReadSignalRPayload = {
  conversationId: string
  messageId: string
  readByUserId: string
  readAt: string
}

/** Payload for messageUnread events */
export type MessageUnreadSignalRPayload = {
  conversationId: string
  messageId: string
  unreadByUserId: string
  unreadAt: string
}

/** Payload for messageEdited events */
export type MessageEditedSignalRPayload = {
  conversationId: string
  messageId: string
  content: string
  editedAt: string
  senderUserId: string
  timestamp: string
}

/** Payload for newAnnouncement events */
export type NewAnnouncementSignalRPayload = {
  id: string
  title: string
  senderName: string
  roles: string[]
  studentStatuses: string[] | null
  groupIds: string[] | null
  publishedAt: string
}

/** Payload for broadcastSendingComplete events */
export type BroadcastSendingCompletePayload = {
  broadcastMessageId: string
  broadcastConversationId: string
  totalRecipients: number
  successCount: number
  failedCount: number
  failedRecipients: Array<{ memberId: string; memberName: string }>
}
